import { Box, Paper, Typography } from '@mui/material';
import { isDynamicLoadError } from '@pn/services/loggers/AppSignalErrorBoundary';
import type { ErrorPageProps } from '@pn/services/loggers/AppSignalErrorBoundaryProvider';
import { CustomButton } from '@pn/ui/custom-components/CustomButton';
import { isNil } from 'lodash-es';
import { BackgroundImage } from './BackgroundImage';

const containerSx = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  height: '100%',
  p: 2,
} as const;

const wrapperSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: { xs: 'flex-start', sm: 'center' },
  gap: 2,
  maxWidth: 1200,
  p: { xs: 2, sm: 4 },
} as const;

const titleSx = {
  typography: { xs: 'h5', sm: 'h4' },
} as const;

const subtitleSx = {
  typography: { xs: 'body1', sm: 'h6' },
  fontWeight: 300,
} as const;

const errorMessageSx = {
  width: '100%',
  overflow: 'auto',
  typography: { xs: 'body2', sm: 'body1' },
  color: 'error.light',
} as const;

export function ErrorPage({ error }: ErrorPageProps) {
  if (isNil(error)) return null;

  return (
    <Box sx={containerSx}>
      <BackgroundImage />
      {isDynamicLoadError(error) ? (
        <Paper variant="outlined" sx={wrapperSx}>
          <Typography sx={titleSx}>Outdated version</Typography>
          <Typography sx={subtitleSx}>
            You are using an outdated version of the app. Please reload this
            page.
          </Typography>
          <Typography sx={errorMessageSx}>
            <code>{error.message}</code>
          </Typography>
          <Box sx={{ width: '100%', textAlign: 'center' }}>
            <CustomButton size="large" onClick={() => window.location.reload()}>
              Reload
            </CustomButton>
          </Box>
        </Paper>
      ) : (
        <Paper variant="outlined" sx={wrapperSx}>
          <Typography sx={titleSx}>An error happened</Typography>
          <Typography sx={subtitleSx}>
            Our team has been notified and is probably already working on it.
          </Typography>
          <Typography sx={errorMessageSx}>
            <code>{error.message}</code>
          </Typography>
          <Typography
            sx={{
              ...errorMessageSx,
              opacity: 0.8,
              wordBreak: 'break-all',
            }}
          >
            {formatErrorStack(error.stack).map((line, i) => (
              <code key={i} style={{ display: 'block', marginBottom: 4 }}>
                {line}
              </code>
            ))}
          </Typography>
          <Box sx={{ width: '100%', textAlign: 'center' }}>
            <CustomButton
              size="large"
              onClick={() => window.location.replace('/')}
            >
              Go back
            </CustomButton>
          </Box>
        </Paper>
      )}
    </Box>
  );
}

function formatErrorStack(stack: string | undefined): string[] {
  if (isNil(stack) || stack === '') {
    return [];
  } else {
    return stack.split('\n').slice(1, 4).concat('...');
  }
}
